import { useState, useEffect } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Pencil, UserX } from "lucide-react";
import { toast } from "sonner";
import { validateCodiceFiscale, validatePartitaIVA } from "@/lib/validators";

function CustomerForm({ customer, onSave, onClose }) {
  const [form, setForm] = useState(customer || {
    name: "", company_name: "", fiscal_code: "", vat_number: "",
    address: "", city: "", province: "", cap: "", phone: "", email: "", notes: ""
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    const cfResult = validateCodiceFiscale(form.fiscal_code);
    if (!cfResult.valid) e.fiscal_code = cfResult.error;
    const pivaResult = validatePartitaIVA(form.vat_number);
    if (!pivaResult.valid) e.vat_number = pivaResult.error;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      if (customer?.id) {
        await api.put(`/customers/${customer.id}`, form);
        toast.success("Cliente aggiornato");
      } else {
        await api.post("/customers", form);
        toast.success("Cliente creato");
      }
      onSave();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const F = (name, label, type = "text") => (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-zinc-500">{label}</Label>
      <Input
        data-testid={`customer-${name}`}
        type={type}
        value={form[name] || ""}
        onChange={(e) => { setForm({ ...form, [name]: e.target.value }); setErrors({ ...errors, [name]: undefined }); }}
        className={`rounded-sm h-9 ${errors[name] ? "border-red-400" : ""}`}
      />
      {errors[name] && <p className="text-xs text-red-500">{errors[name]}</p>}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {F("name", "Nome *")}
        {F("company_name", "Ragione Sociale")}
        {F("fiscal_code", "Codice Fiscale")}
        {F("vat_number", "Partita IVA")}
        {F("address", "Indirizzo")}
        {F("city", "Città")}
        {F("province", "Provincia")}
        {F("cap", "CAP")}
        {F("phone", "Telefono", "tel")}
        {F("email", "Email", "email")}
      </div>
      <div className="space-y-1">
        <Label className="text-xs font-medium text-zinc-500">Note</Label>
        <textarea
          data-testid="customer-notes"
          value={form.notes || ""}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="input-field min-h-[60px] resize-y"
          rows={2}
        />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Annulla</Button>
        <Button type="submit" disabled={saving || !form.name} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="customer-save-btn">
          {saving ? "Salvataggio..." : "Salva"}
        </Button>
      </div>
    </form>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const { data } = await api.get(`/customers?${params}`);
      setCustomers(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, [search]);

  const handleDeactivate = async (id) => {
    if (!window.confirm("Disattivare questo cliente?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Cliente disattivato");
      fetchCustomers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="customers-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tighter font-['Outfit']">Clienti</h1>
          <p className="text-sm text-zinc-500">{total} clienti totali</p>
        </div>
        <Button
          data-testid="add-customer-btn"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="bg-[#002FA7] hover:bg-[#002482] rounded-sm"
        >
          <Plus className="w-4 h-4 mr-1" /> Nuovo Cliente
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input
          data-testid="customer-search"
          placeholder="Cerca cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 rounded-sm"
        />
      </div>

      <div className="card-surface overflow-hidden">
        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Nome</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden sm:table-cell">Azienda</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden md:table-cell">Città</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden md:table-cell">Email</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden md:table-cell">Telefono</th>
                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50/50 transition-colors" data-testid={`customer-row-${c.id}`}>
                  <td className="px-5 py-3 text-sm font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden sm:table-cell">{c.company_name || "-"}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden md:table-cell">{c.city || "-"}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden md:table-cell">{c.email || "-"}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden md:table-cell">{c.phone || "-"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`edit-customer-${c.id}`}
                        onClick={() => { setEditing(c); setDialogOpen(true); }}
                        className="p-1.5 hover:bg-zinc-100 rounded-sm transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5 text-zinc-500" />
                      </button>
                      <button
                        data-testid={`deactivate-customer-${c.id}`}
                        onClick={() => handleDeactivate(c.id)}
                        className="p-1.5 hover:bg-red-50 rounded-sm transition-colors"
                      >
                        <UserX className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && customers.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-zinc-400">Nessun cliente trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-zinc-100">
          {customers.map((c) => (
            <div key={c.id} className="p-4" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{c.name}</p>
                  {c.company_name && <p className="text-xs text-zinc-500 mt-0.5">{c.company_name}</p>}
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-zinc-400">
                    {c.city && <span>{c.city}</span>}
                    {c.phone && <span>{c.phone}</span>}
                    {c.email && <span className="truncate max-w-[180px]">{c.email}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-testid={`edit-customer-${c.id}`}
                    onClick={() => { setEditing(c); setDialogOpen(true); }}
                    className="p-2 hover:bg-zinc-100 rounded-sm transition-colors"
                  >
                    <Pencil className="w-4 h-4 text-zinc-500" />
                  </button>
                  <button
                    data-testid={`deactivate-customer-${c.id}`}
                    onClick={() => handleDeactivate(c.id)}
                    className="p-2 hover:bg-red-50 rounded-sm transition-colors"
                  >
                    <UserX className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {!loading && customers.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-zinc-400">Nessun cliente trovato</div>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{editing ? "Modifica Cliente" : "Nuovo Cliente"}</DialogTitle>
          </DialogHeader>
          <CustomerForm
            customer={editing}
            onSave={() => { setDialogOpen(false); fetchCustomers(); }}
            onClose={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
